// client/src/pages/RoutesCost/components/Form.jsx
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Library from '../../../Library'
import ButtonsBlock from '../../../components/ButtonsBlock/ButtonsBlock'
import LinearLoader from '../../../components/Loaders/LinearLoader'
import { ActionsWrapper, CommentWrapper, FormWrapper, RowWrapper, SectionWrapper, Table, Tbody, Td, Th, Thead, Tr, Wrapper } from './styledComponents'
import { useMessage } from '../../../hooks/useMessage'
import TextInput from '../../../components/Inputs/TextInput'
import Button from '../../../components/Buttons/Button'
import useWindowHeight from '../../../hooks/useWindowHeight'
import Textarea from '../../../components/Inputs/Textarea'
import * as XLSX from 'xlsx-js-style'
import { nanoid } from 'nanoid'
import SimpleTextInput from '../../../components/Inputs/SimpleTextInput'
import { useAuth } from '../../../hooks/useAuth'
import ModalContext from '../../../context/ModalContext'
import ConfirmationWindow from './ConfirmationWindow'

export const Form = () => {
  const location = useLocation()
  const pageName = location.pathname.substring(1).split('/')[0]
  const params = useParams()
  const message = useMessage()
  const wrapperRef = useRef(null)
  useWindowHeight(wrapperRef, 84)
  const debounceDelay = 500
  const { companyId, routesCost, loading, error } = useSelector(state => state.data)
  const isDataLoaded = useSelector(state => state.loading.isDataLoaded)
  const [dataReady, setDataReady] = useState(false)

  const { roles } = useAuth()

  const { showModal, setModalContent } = useContext(ModalContext)

  const hasRole = (roleValue) =>
    roles?.some(role =>
      role.value === 'admin' ||
      (
        role.companyId === companyId &&
        role.value === roleValue
      )
    )

  const canEdit = hasRole('routes-cost-edit')
  const canDelete = hasRole('routes-cost-delete')

  const [search, setSearch] = useState('')

  const initialFormData = useMemo(() => ({
    id: params.id,
    name: '',
    comment: '',
    routes: [] // массив маршрутов
  }), [params.id])

  const [form, setForm] = useState(initialFormData)

  // --- Загружаем данные, если редактирование ---
  useEffect(() => {
    if (isDataLoaded && !loading && !error) {
      if (params.id) {
        const formData = Library.findItemById(routesCost, params.id) // <-- используем findItemById
        if (formData) {
          setForm({
            ...formData,
            routes: formData.routes
              ? JSON.parse(formData.routes)
                .map(route => ({
                  id: route.id || nanoid(), // ← добавляем id если нет
                  ...route
                }))
                .sort((a, b) =>
                  (a.start_address || '').localeCompare(b.start_address || '', 'ru', {
                    sensitivity: 'base'
                  })
                )
              : []
          })
        } else {
          setForm(initialFormData)
        }
      }
      setDataReady(true)
    }
  }, [initialFormData, isDataLoaded, loading, error, routesCost, params.id])

  const normalizeText = value => {
    if (typeof value !== 'string') return value

    return value
      .trim()
      .replace(/[ \t]+/g, ' ')
  }

  const normalizeForm = form => ({
    ...form,
    name: normalizeText(form.name),
    comment: normalizeText(form.comment),
    routes: (form.routes || []).map(route => ({
      ...route,
      start_address: normalizeText(route.start_address),
      end_address: normalizeText(route.end_address),
      customer_rate_net: normalizeText(route.customer_rate_net),
      customer_rate_gross: normalizeText(route.customer_rate_gross),
      comment: normalizeText(route.comment)
    }))
  })

  // --- Изменение полей формы ---
  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  // --- Изменение строки маршрута ---
  const handleRouteChange = (id, key, value) => {
    setForm(prev => ({
      ...prev,
      routes: prev.routes.map(r =>
        r.id === id ? { ...r, [key]: value } : r
      )
    }))
  }

  // --- Добавление новой строки ---
  const handleAddRow = () => {
    setForm(prev => ({
      ...prev,
      routes: [
        ...prev.routes,
        {
          id: nanoid(),
          start_address: '',
          end_address: '',
          customer_rate_net: '',
          customer_rate_gross: '',
          comment: ''
        }
      ]
    }))
  }

  // --- Удаление строки ---
  const handleDeleteRow = (id) => {
    setForm(prev => ({
      ...prev,
      routes: prev.routes.filter(r => r.id !== id)
    }))
  }

  const handleSearchChange = value => {
    if (typeof value === 'string') {
      setSearch(value)
    } else if (value?.target?.value !== undefined) {
      setSearch(value.target.value)
    } else {
      setSearch('')
    }
  }

  const handleDownloadExcel = () => {
    if (!form.routes || form.routes.length === 0) return

    const data = form.routes.map(route => ({
      'Начало маршрута': route.start_address,
      'Конец маршрута': route.end_address,
      'Ставка заказчика (нал. и без НДС)': route.customer_rate_net,
      'Ставка заказчика (с НДС)': route.customer_rate_gross,
      'Комментарий': route.comment
    }))

    const worksheet = XLSX.utils.json_to_sheet(data)

    /* ---------- ширина колонок ---------- */
    worksheet['!cols'] = [
      { wch: 30 }, // Начало маршрута
      { wch: 30 }, // Конец маршрута
      { wch: 25 }, // без НДС
      { wch: 25 }, // с НДС
      { wch: 40 }  // Комментарий
    ]

    /* ---------- стили ---------- */
    const range = XLSX.utils.decode_range(worksheet['!ref'])

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C })
        const cell = worksheet[cellRef]
        if (!cell) continue

        cell.s = {
          border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
          },
          alignment: {
            vertical: 'center',
            wrapText: true
          }
        }

        // header
        if (R === 0) {
          cell.s.font = { bold: true }
          cell.s.fill = {
            patternType: 'solid',
            fgColor: { rgb: 'E0E0E0' }
          }
          cell.s.alignment.horizontal = 'center'
        }
      }
    }

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Прайс-лист')

    XLSX.writeFile(workbook, `${form.name || 'routes-cost'}.xlsx`)
  }

  const filteredRoutes = useMemo(() => {
    const query = String(search ?? '').trim().toLowerCase()
    if (!query) return form.routes

    return form.routes.filter(route =>
      Object.values(route).some(value =>
        String(value ?? '').toLowerCase().includes(query)
      )
    )
  }, [form.routes, search])

  if (!dataReady) return <LinearLoader isLoading={!dataReady} style={{ height: '4px' }} />

  return (
    <>
      <LinearLoader
        isLoading={!dataReady || !isDataLoaded}
        style={{ height: '4px' }}
      />
      <Wrapper ref={wrapperRef}>
        {isDataLoaded && dataReady &&
          <FormWrapper $maxWidth={1650}>
            <SectionWrapper $maxWidth={1000}>
              <RowWrapper>
                <TextInput
                  label='Название'
                  name='name'
                  id='name'
                  value={form.name}
                  isClearable={true}
                  debounceDelay={debounceDelay}
                  onChange={e => handleChange('name', e.target.value)}
                />
              </RowWrapper>
            </SectionWrapper>

            {/* --- TABLE --- */}
            <Table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse' }}>
              <colgroup>
                <col style={{ width: '4%' }} />   {/* Индекс */}
                <col style={{ width: '25%' }} />  {/* Начало маршрута */}
                <col style={{ width: '25%' }} />  {/* Конец маршрута */}
                <col style={{ width: '15%' }} />  {/* Ставка заказчика (нал. и без НДС) */}
                <col style={{ width: '15%' }} />  {/* Ставка заказчика (с НДС) */}
                <col style={{ width: '15%' }} />  {/* Комментарий */}
                <col style={{ width: '5%' }} />   {/* Действия */}
              </colgroup>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Начало маршрута</Th>
                  <Th>Конец маршрута</Th>
                  <Th>Ставка заказчика ( нал. и без НДС )</Th>
                  <Th>Ставка заказчика ( с НДС )</Th>
                  <Th>Комментарий</Th>
                  <Th>Действия</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRoutes.map((route, index) => (
                  <Tr key={route.id}>
                    <Td $padding="0 8px">{index + 1}</Td> {/* индекс */}
                    <Td>
                      <SimpleTextInput value={route.start_address} onChange={e => handleRouteChange(route.id, 'start_address', e.target.value)} isClearable={true} readOnly={!canEdit} searchValue={search} />
                    </Td>
                    <Td>
                      <SimpleTextInput value={route.end_address} onChange={e => handleRouteChange(route.id, 'end_address', e.target.value)} isClearable={true} readOnly={!canEdit} searchValue={search}/>
                    </Td>
                    <Td>
                      <SimpleTextInput value={route.customer_rate_net} onChange={e => handleRouteChange(route.id, 'customer_rate_net', e.target.value)} isClearable={true} readOnly={!canEdit} searchValue={search}/>
                    </Td>
                    <Td>
                      <SimpleTextInput value={route.customer_rate_gross} onChange={e => handleRouteChange(route.id, 'customer_rate_gross', e.target.value)} isClearable={true} readOnly={!canEdit} searchValue={search}/>
                    </Td>
                    <Td>
                      <SimpleTextInput value={route.comment} onChange={e => handleRouteChange(route.id, 'comment', e.target.value)} isClearable={true} readOnly={!canEdit} searchValue={search}/>
                    </Td>
                    <Td $padding="0px">
                      <Button
                        style={{ backgroundColor: '#E57373', height: '22px' }}
                        disabled={!canEdit}
                        onClick={() => {
                          setModalContent(
                            <ConfirmationWindow
                              text={`Удалить маршрут: ${route.start_address} → ${route.end_address}?`}
                              onConfirm={() => handleDeleteRow(route.id)}
                            />
                          )
                          showModal()
                        }}
                      >
                        X
                      </Button>

                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            <ActionsWrapper>
              <TextInput
                placeholder='Поиск…'
                value={search}
                isClearable={true}
                debounceDelay={300}
                onChange={handleSearchChange}
              />

              <Button onClick={handleAddRow} style={{ width: '150px' }} disabled={!canEdit}>
                + Добавить
              </Button>

              <Button
                onClick={handleDownloadExcel}
                style={{ width: '150px', backgroundColor: '#4CAF50' }}
              >
                Скачать Excel
              </Button>
            </ActionsWrapper>

            <SectionWrapper $maxWidth={1000}>
              <CommentWrapper>
                <RowWrapper>
                  <Textarea
                    label='Комментарий'
                    name='comment'
                    id='comment'
                    value={form.comment}
                    debounceDelay={debounceDelay}
                    onChange={e => handleChange('comment', e.target.value)}
                  />
                </RowWrapper>
              </CommentWrapper>
            </SectionWrapper>

            {/* --- BUTTONS BLOCK --- */}
            <ButtonsBlock
              handler={Library.handler}
              form={normalizeForm(form)}
              edit={!!params.id}
              label={form.name}
              message={message}
              fileFolder={pageName}
              path='data'
              request={pageName}
              changeHistoryTableName={`${companyId}_change_history`}
              changeHistoryId={form.id}
              canDelete={canDelete}
              canEdit={canEdit}
            />
          </FormWrapper>
        }
      </Wrapper>
    </>
  )
}
